<template>
    <div
        id="bbva-form-1"
        class="p-4 md:p-6 shadow"
        :class="formComplete ? 'bg-light-blue' : 'bg-gray2'"
    >
        <div
            class="flex"
            :class="actualForm > 1 ? 'cursor-pointer' : 'cursor-default'"
            @click="onFormClick"
        >
            <div>
                <div
                    class="rounded-full w-5 lg:w-6 text-center text-sm lg:text-base leading-none py-1 font-medium"
                    :class="[formComplete ? ['bg-white'] : ['bg-light-blue', 'text-white']]"
                >1</div>
            </div>
            <div
                class="ml-4"
                :class="formComplete ? 'text-white' : ''"
            >
                <p class="text-lg sm:text-2xl font-medium leading-none xs:leading-tight">Datos personales</p>
                <p class="text-sm leading-tight mt-1 xs:mt-0">Recuerda que estos datos son obligatorios.</p>
            </div>
            <div class="ml-auto mt-2">
                <div
                    v-if="!formComplete"
                    :key="showForm"
                    :class="showForm ? 'triangle-up-black' : 'triangle-down-black'"
                ></div>
                <div
                    v-else
                    :key="showForm"
                    :class="showForm ? 'triangle-up-white' : 'triangle-down-white'"
                ></div>
            </div>
        </div>
        <div
            class="form-1 mt-4"
            :class="{'hidden': !showForm}"
        >
            <div class="line border-solid border-b border-gray3"></div>
            <ValidationObserver v-slot="{ handleSubmit }">
                <form
                    class="mt-4 xs:w-4/6 sm:w-5/6 lg:w-4/6 mx-auto leading-tight sm:leading-normal"
                    @submit.prevent="handleSubmit(onSubmit)"
                >
                    <div class="flex flex-col space-y-2">
                        <div class="flex flex-col space-y-2 sm:space-y-0 sm:flex-row sm:space-x-4 w-full">
                            <ValidationProvider
                                class="sm:w-1/2"
                                name="curp"
                                rules="required|curp"
                                v-slot="{ errors, classes }"
                            >
                                <input-form
                                    :classes="classes"
                                    :type="'text'"
                                    :name="'curp'"
                                    :value="formInputs.curp"
                                    @input="onCurpChange"
                                    :key="`curp-${inputsKeys.curp}`"
                                    :disabled="disabledInputs.curp"
                                >
                                    CURP*
                                </input-form>
                                <span class="text-sm text-red">{{ errors[0] }}</span>
                            </ValidationProvider>
                            <ValidationProvider
                                class="sm:w-1/2"
                                rules="required|length:13"
                                v-slot="{ errors, classes }"
                            >
                                <input-form
                                    :classes="classes"
                                    :type="'text'"
                                    :name="'rfc'"
                                    :value="formInputs.rfc"
                                    @input="onRfcChange"
                                >
                                    RFC con homoclave*
                                </input-form>
                                <span class="text-sm text-red">{{ errors[0] }}</span>
                            </ValidationProvider>
                        </div>
                        <div class="flex flex-col space-y-2 sm:space-y-0 sm:flex-row sm:space-x-4 w-full">
                            <ValidationProvider
                                class="sm:w-1/2"
                                :rules="`alpha_spaces|required${firstNameRule}`"
                                v-slot="{ errors, classes }"
                            >
                                <input-form
                                    :classes="classes"
                                    :type="'text'"
                                    :name="'first_name'"
                                    :value="formInputs.firstName"
                                    @input="formInputs.firstName = formatInput($event)"
                                >
                                    Primer nombre*
                                </input-form>
                                <span class="text-sm text-red">{{ errors[0] }}</span>
                            </ValidationProvider>
                            <ValidationProvider
                                class="sm:w-1/2"
                                :rules="`alpha_spaces${middleNameRule}`"
                                v-slot="{ errors, classes }"
                            >
                                <input-form
                                    :classes="classes"
                                    :type="'text'"
                                    :name="'middle_name'"
                                    :value="formInputs.middleName"
                                    @input="formInputs.middleName = formatInput($event)"
                                >
                                    Segundo nombre
                                </input-form>
                                <span class="text-sm text-red">{{ errors[0] }}</span>
                            </ValidationProvider>
                        </div>
                        <div class="flex flex-col space-y-2 sm:space-y-0 sm:flex-row sm:space-x-4 w-full">
                            <ValidationProvider
                                class="sm:w-1/2"
                                rules="required|last_name_curp:@curp"
                                v-slot="{ errors, classes }"
                            >
                                <input-form
                                    :classes="classes"
                                    :type="'text'"
                                    :name="'last_name'"
                                    :value="formInputs.lastName"
                                    @input="formInputs.lastName = formatInput($event)"
                                >
                                    Apellido paterno*
                                </input-form>
                                <span class="text-sm text-red">{{ errors[0] }}</span>
                            </ValidationProvider>
                            <ValidationProvider
                                class="sm:w-1/2"
                                rules="required|last_mname_curp:@curp"
                                v-slot="{ errors, classes }"
                            >
                                <input-form
                                    :classes="classes"
                                    :type="'text'"
                                    :name="'last_mothers_name'"
                                    :value="formInputs.lastMothersName"
                                    @input="formInputs.lastMothersName = formatInput($event)"
                                >
                                    Apellido materno*
                                </input-form>
                                <span class="text-sm text-red">{{ errors[0] }}</span>
                            </ValidationProvider>
                        </div>
                        <div class="flex flex-col space-y-2 sm:space-y-0 sm:flex-row sm:space-x-4 w-full">
                            <ValidationProvider
                                class="sm:w-1/2"
                                rules="gender_curp:@curp"
                                v-slot="{ errors, classes, invalid }"
                            >
                                <v-select-form
                                    :name="'gender'"
                                    :invalid="invalid"
                                    :classes="classes"
                                    :options="catalogs.genders"
                                    :loading="loading.genders"
                                    v-model="formInputs.gender"
                                >
                                    Género
                                </v-select-form>
                                <span class="text-sm text-red">{{ errors[0] }}</span>
                            </ValidationProvider>
                            <ValidationProvider
                                class="sm:w-1/2"
                                v-slot="{ errors, classes, invalid }"
                            >
                                <v-select-form
                                    :name="'marital_status'"
                                    :invalid="invalid"
                                    :classes="classes"
                                    :options="catalogs.maritalStatus"
                                    :loading="loading.maritalStatus"
                                    v-model="formInputs.mStatus"
                                >
                                    Estado civil
                                </v-select-form>
                                <span class="text-sm text-red">{{ errors[0] }}</span>
                            </ValidationProvider>
                        </div>
                        <div class="flex flex-col space-y-2 sm:space-y-0 sm:flex-row sm:space-x-4 w-full">
                            <ValidationProvider
                                class="sm:w-1/2"
                                rules="required"
                                v-slot="{ errors, classes, invalid }"
                            >
                                <v-select-form
                                    :name="'nationality'"
                                    :invalid="invalid"
                                    :classes="classes"
                                    :options="catalogs.nationalities"
                                    :loading="loading.nationalities"
                                    :value="formInputs.nationality"
                                    @input="onNatChange"
                                >
                                    Nacionalidad*
                                </v-select-form>
                                <span class="text-sm text-red">{{ errors[0] }}</span>
                            </ValidationProvider>
                            <ValidationProvider
                                class="sm:w-1/2"
                                rules="required"
                                v-slot="{ errors, classes, invalid }"
                            >
                                <v-select-form
                                    :name="'country'"
                                    :invalid="invalid"
                                    :classes="classes"
                                    :options="catalogs.countries"
                                    :loading="loading.countries"
                                    :disabled="disabledInputs.country"
                                    v-model="formInputs.country"
                                >
                                    País de nacimiento*
                                </v-select-form>
                                <span class="text-sm text-red">{{ errors[0] }}</span>
                            </ValidationProvider>
                        </div>
                        <div class="flex flex-col space-y-2 sm:space-y-0 sm:flex-row sm:space-x-4 w-full">
                            <ValidationProvider
                                class="sm:w-1/2"
                                rules="required|birthdate|birthdate_curp:@curp"
                                v-slot="{ errors, classes }"
                            >
                                <input-form
                                    :classes="classes"
                                    :type="'date'"
                                    :name="'birth_date'"
                                    :minmax="{min: minDate,max: maxDate}"
                                    :value="formInputs.birthDate"
                                    @input="formInputs.birthDate = $event"
                                >
                                    Fecha de nacimiento*
                                </input-form>
                                <span class="text-sm text-red">{{ errors[0] }}</span>
                            </ValidationProvider>
                            <ValidationProvider
                                class="sm:w-1/2"
                                rules="required"
                                v-slot="{ errors, classes, invalid }"
                            >
                                <v-select-form
                                    :name="'state'"
                                    :invalid="invalid"
                                    :classes="classes"
                                    :options="catalogs.states"
                                    :loading="loading.states"
                                    :disabled="disabledInputs.state"
                                    v-model="formInputs.state"
                                >
                                    Estado de nacimiento*
                                </v-select-form>
                                <span class="text-sm text-red">{{ errors[0] }}</span>
                            </ValidationProvider>
                        </div>
                        <div class="flex flex-col space-y-2 sm:space-y-0 sm:flex-row sm:space-x-4 w-full">
                            <ValidationProvider
                                class="sm:w-1/2"
                                rules="required"
                                v-slot="{ errors, classes, invalid }"
                            >
                                <v-select-form
                                    :name="'id_type'"
                                    :invalid="invalid"
                                    :classes="classes"
                                    :options="catalogs.idTypes"
                                    :loading="loading.idTypes"
                                    :value="formInputs.idType"
                                    @input="onIdTypeChange"
                                >
                                    Tipo de indentificación*
                                    <button
                                        class="ml-1"
                                        type="button"
                                        v-show="infoModalImage"
                                        @click="showInfoModal = true"
                                    >
                                        <span class="text-light-blue text-sm">
                                            <i class="fa-solid fa-circle-info"></i>
                                        </span>
                                    </button>
                                </v-select-form>
                                <span class="text-sm text-red">{{ errors[0] }}</span>
                            </ValidationProvider>
                            <ValidationProvider
                                class="sm:w-1/2"
                                :rules="`required${idTypeRule}`"
                                v-slot="{ errors, classes }"
                            >
                                <input-form
                                    :classes="classes"
                                    :type="'text'"
                                    :name="'id_num'"
                                    :value="formInputs.idNum"
                                    @input="formInputs.idNum = $event"
                                >
                                    Número de identificación*
                                </input-form>
                                <span class="text-sm text-red">{{ errors[0] }}</span>
                            </ValidationProvider>
                        </div>
                    </div>
                    <div class="mt-6 flex flex-col space-y-2 items-center justify-end sm:flex-row sm:space-y-0 sm:space-x-2">
                        <button
                            type="button"
                            class="hyundai-button w-40 py-0 h-8 bg-light-blue"
                            @click="saveProgress"
                            :disabled="savingProgress"
                        >{{ !savingProgress ? 'Guardar avance' : 'Guardando...' }}</button>
                        <button
                            type="submit"
                            class="hyundai-button w-40 py-0 h-8"
                        >Siguiente</button>
                    </div>
                </form>
            </ValidationObserver>
        </div>
        <modal-form
            :show="showInfoModal"
            @close="showInfoModal = false"
        >
            <p>{{ infoModalText }}</p>
            <img
                class="mt-4"
                :class="formInputs.idType.id === 'CI' ? 'w-full' : 'w-full sm:w-8/12'"
                :src="infoModalImage"
                :alt="infoModalText"
            >
            <!-- <picture class="w-11/12">
                <source
                    :srcset="`${url}/images/bbva-form-f2/ine_ocr_mobile.png`"
                    media="(max-width: 768px)"
                >
                <source :srcset="`${url}/images/bbva-form-f2/ine_ocr_desktop.png`">
                <img
                    :src="`${url}/images/bbva-form-f2/ine_ocr_mobile.png`"
                    alt="Ejemplo de OCR en INE"
                >
            </picture> -->
        </modal-form>
    </div>
</template>

<script>
// Componentes para las validaciones del formulario
import {
    ValidationProvider,
    ValidationObserver,
    setInteractionMode,
} from "vee-validate";
setInteractionMode("eager");
// Validaciones personalizadas para el formulario
import "../helpers/form_validations";
// Helpers
import {
    formatDate,
    fourDigitsYear,
    getCatalog,
    getEnum,
} from "../helpers/helpers_functions";

export default {
    components: {
        ValidationProvider,
        ValidationObserver,
    },
    props: ["mainCheck", "actualForm", "savingProgress"],
    data() {
        return {
            url: $(".url").text(),
            showForm: false, // Variable para mostrar u ocultar el formulario
            formComplete: false, // Variable para validar que el formulario esté completo
            // Campos del formulario
            formInputs: {
                curp: "",
                rfc: "",
                lastName: "",
                lastMothersName: "",
                firstName: "",
                middleName: "",
                birthDate: "",
                gender: "",
                state: "",
                nationality: "",
                country: "",
                idNum: "",
                idType: "",
                mStatus: "",
            },
            // Variable para recargar el componente del curp
            // cuando se genera el evento input
            inputsKeys: {
                curp: 0,
            },
            // Variables para deshabilitar inputs
            disabledInputs: {
                state: false,
                country: false,
                curp: false,
            },
            idTypeRule: "", // Reglas para validar input de núm id
            // Variables para los catálogos importados
            catalogs: {
                genders: [],
                maritalStatus: [],
                nationalities: [],
                idTypes: [],
                countries: [],
                states: [],
            },
            // Variables para determinar la carga de los catálogos
            loading: {
                genders: false,
                nationalities: false,
                countries: false,
                states: false,
                maritalStatus: false,
                idTypes: false,
            },
            showInfoModal: false,
            infoModalText: "",
            infoModalImage: "",
        };
    },
    watch: {
        // Función para mostrar u ocultar el formulario con jQuery
        showForm(newVal) {
            if (newVal) {
                this.$emit("form-open", 1);
                $(".form-1").slideDown();
                if (this.emptyCatalog) {
                    this.getCatalogs();
                    this.getEnums();
                }
            } else {
                this.$emit("form-closed", 1);
                $(".form-1").slideUp();
            }
        },
    },
    methods: {
        getCatalogs() {
            this.loading.nationalities = true;
            getCatalog("NATIONALITIES")
                .then((resp) => {
                    this.catalogs.nationalities = resp;
                    this.loading.nationalities = false;
                })
                .catch((err) => {
                    console.log(err);
                    this.loading.nationalities = false;
                });

            this.loading.countries = true;
            getCatalog("COUNTRIES")
                .then((resp) => {
                    this.catalogs.countries = resp;
                    this.loading.countries = false;
                })
                .catch((err) => {
                    console.log(err);
                    this.loading.countries = false;
                });

            this.loading.states = true;
            getCatalog("STATES_CUSTOMER")
                .then((resp) => {
                    this.catalogs.states = resp;
                    this.loading.states = false;
                })
                .catch((err) => {
                    console.log(err);
                    this.loading.states = false;
                });
        },
        getEnums() {
            this.loading.idTypes = true;
            getEnum("documentType")
                .then((resp) => {
                    this.catalogs.idTypes = resp;
                    this.loading.idTypes = false;
                })
                .catch((err) => {
                    console.log(err);
                    this.loading.idTypes = false;
                });

            this.loading.maritalStatus = true;
            getEnum("maritalStatus")
                .then((resp) => {
                    this.catalogs.maritalStatus = resp;
                    this.loading.maritalStatus = false;
                })
                .catch((err) => {
                    console.log(err);
                    this.loading.maritalStatus = false;
                });

            this.loading.genders = true;
            getEnum("gender")
                .then((resp) => {
                    this.catalogs.genders = resp;
                    this.loading.genders = false;
                })
                .catch((err) => {
                    console.log(err);
                    this.loading.genders = false;
                });
        },
        onFormClick() {
            if (this.mainCheck && !this.showForm && this.actualForm > 0) {
                this.$eventBus.$emit("hide-forms");
                this.showForm = true;
                this.formComplete = false;
            }
        },
        formatInput(value) {
            return value
                .toUpperCase()
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "");
        },
        // Función para escuhcar los cambios en el select de nacionalidad
        onNatChange(value) {
            this.formInputs.nationality = value;
            // Si la nacionalidad es Extranjera el estado debe ser "DF"
            // además se desactiva el select de estado para que se quede fijo
            // en caso contrario selecciona el país méxico
            // habilita el select de estado y curp, y deshabilita el select de país
            if (value.id === "131") {
                this.formInputs.state = "DF";
                this.disabledInputs.state = true;
                this.formInputs.country = "";
                this.disabledInputs.country = false;
                // Si ya está seleccionado el genéro coloca el
                // curp genérico correspondiente
                if (this.formInputs.gender.id) {
                    this.formInputs.gender.id === "M"
                        ? (this.formInputs.curp = "XEXX010101HNEXXXA4") // Curp genérico para hombre
                        : (this.formInputs.curp = "XEXX010101MNEXXXA8"); // Curp genérico para mujer
                    this.disabledInputs.curp = true;
                }
            } else {
                this.disabledInputs.state = false;
                this.formInputs.country = "MEX";
                this.disabledInputs.country = true;
                this.disabledInputs.curp = false;
                if (
                    this.formInputs.curp.description === "XEXX010101HNEXXXA4" ||
                    this.formInputs.curp.description === "XEXX010101MNEXXXA8"
                ) {
                    this.formInputs.curp = "";
                }
            }
        },
        // Si cambia el genéro y la nacionalidad ya se ha seleccionado
        // se coloca el curp genérico correspondiente y se
        // deshabilita el campo de curp
        onGenderChange(value) {
            this.formInputs.gender = value;
            if (this.formInputs.nationality.id === "131") {
                value === "M"
                    ? (this.formInputs.curp = "XEXX010101HNEXXXA4") // Curp genérico para hombre
                    : (this.formInputs.curp = "XEXX010101MNEXXXA8"); // Curp genérico para mujer
                this.disabledInputs.curp = true;
            }
        },
        // Función para escuchar los cambios sobre el select de tipo de id
        // y seleccionar las reglas correspondientes para el campo de núm de id
        onIdTypeChange(value) {
            this.formInputs.idType = value;
            // this.formInputs.idNum = "";
            switch (value.id) {
                case "CI":
                    this.idTypeRule = "|numeric|length:8";
                    this.infoModalText = "Ejemplo de cédula profesional";
                    this.infoModalImage = `${this.url}/images/bbva-form-f2/cedula.jpg`;
                    break;
                case "INE":
                    this.idTypeRule = "|numeric|length:13";
                    this.infoModalText =
                        'Debes colocar el OCR (2) en el campo "Número de identificación"';
                    this.infoModalImage = `${this.url}/images/bbva-form-f2/ine_ocr_mobile.png`;
                    break;
                case "PASSPORT":
                    this.idTypeRule = "|alpha_num|length:9";
                    this.infoModalText = "Ejemplo de pasaporte";
                    this.infoModalImage = `${this.url}/images/bbva-form-f2/pasaporte.png`;
                    break;
                case "FOREIGNERS":
                    this.idTypeRule = "|numeric|length:13";
                    this.infoModalText = "Ejemplo de forma migratoria";
                    this.infoModalImage = `${this.url}/images/bbva-form-f2/forma_migratoria.png`;
                    break;
                case "ORIGIN_IDENTITY_DOCUMENT":
                    this.idTypeRule = "|numeric|length:13";
                    this.infoModalText = "Ejemplo de forma migratoria";
                    this.infoModalImage = `${this.url}/images/bbva-form-f2/forma_migratoria.png`;
                    break;
                case "CURP":
                    this.formInputs.idNum = this.formInputs.curp;
                    this.idTypeRule = "|alpha_num|length:18";
                    break;
                default:
                    break;
            }
        },
        // Función para escuchar los cambios en el input de rfc
        // y cambiar los valores a mayúsculas
        onRfcChange(value) {
            this.formInputs.rfc = value.toUpperCase();
        },
        // Función para escuchar los cambios en el input curp
        // y llenar los otros campos a partir de este input
        onCurpChange(value) {
            // Si la longitud es menor o igual a 19 registra el valor
            // convirtiendolo a mayúsculas y registrando el valor en
            // el campo de rfc y los demás campos correspondientes
            // en caso contrario no permite seguir escribiendo
            if (value.length <= 19) {
                this.formInputs.curp = value.toUpperCase();
                this.formInputs.rfc =
                    value.length <= 10
                        ? value.toUpperCase()
                        : value.slice(0, 10);

                // Si la longitud es mayor o igual a 1 registra el primer caracter
                // en el campo de apellido paterno en mayúsculas
                // if (value.length >= 1) {
                //     this.formInputs.lastName = value[0].toUpperCase();
                // }

                // Si la longitud es mayor o igual a 3 registra el primer caracter
                // en el campo de apellido materno en mayúsculas
                // if (value.length >= 3) {
                //     this.formInputs.lastMothersName = value[2].toUpperCase();
                // }

                // Si la longitud es mayor o igual a 4 registra el primer caracter
                // en el campo de primer nombre en mayúsculas
                // if (value.length >= 4) {
                //     this.formInputs.firstName = value[3].toUpperCase();
                // }

                // Si la longitud del curp es mayor o igual a 10 caracteres
                // le da formato a la fecha de nacimiento del curp para
                // llenar el input de fecha de nacimiento
                if (value.length >= 10) {
                    let birthDate = value.slice(4, 10);
                    birthDate = `${fourDigitsYear(
                        parseInt(birthDate.slice(0, 2))
                    )}-${birthDate.slice(2, 4)}-${birthDate.slice(4, 6)}`;
                    this.formInputs.birthDate = birthDate;
                }

                // Si la longitud es mayor o igual a 11 se coloca el valor
                // correspondiente en el campo de género
                if (value.length >= 11) {
                    this.formInputs.gender =
                        value[10] === "H" ? "MALE" : "FEMALE";
                }

                // Si la longitud es mayor o igual a 13 se selecciona
                // el estado correspondiente en el select de estado
                // if (value.length >= 13) {
                //     this.formInputs.state = value.slice(11, 13).toUpperCase();
                // }
            } else {
                this.formInputs.curp = value.slice(0, 19);
                this.inputsKeys.curp++;
                this.$nextTick(() => {
                    $("#curp").focus();
                });
            }
        },
        onSubmit() {
            if (!this.emptyCatalog) {
                // this.addInputsDesc();

                const formData = {
                    formNum: 1,
                    formInputs: { ...this.formInputs },
                };

                this.$emit("form-validated", formData);
            } else {
                this.$emit("charging-catalogs");
            }
        },
        saveProgress() {
            if (!this.emptyCatalog) {
                // this.addInputsDesc();

                const formData = {
                    formNum: 1,
                    formInputs: { ...this.formInputs },
                };

                this.$emit("save-progress", formData);
            } else {
                this.$emit("charging-catalogs");
            }
        },
        setInputs(inputs) {
            this.formInputs.curp = inputs.curp;
            this.formInputs.rfc = inputs.rfc;
            this.formInputs.lastName = inputs.lastName;
            this.formInputs.lastMothersName = inputs.secondLastName;
            this.formInputs.firstName = inputs.firstName;
            this.formInputs.middleName = inputs.middleName;
            this.formInputs.birthDate = inputs.birthDate;
            this.formInputs.gender = inputs.gender_id;
            this.formInputs.state = inputs.birthData_state_id;
            this.formInputs.nationality = inputs.nationality_id;
            this.formInputs.country = inputs.birthData_country_id;
            this.formInputs.idNum = inputs.id_num;
            this.formInputs.idType = inputs.id_type;
            this.formInputs.mStatus = inputs.maritalStatus;
        },
    },
    computed: {
        minDate() {
            let date = new Date();
            date.setFullYear(date.getFullYear() - 71);
            return formatDate(date);
        },
        maxDate() {
            let date = new Date();
            date.setFullYear(date.getFullYear() - 18);
            return formatDate(date);
        },
        emptyCatalog() {
            let empty = false;
            for (const key in this.catalogs) {
                if (!this.catalogs[key].length) {
                    empty = true;
                    break;
                }
            }
            return empty;
        },
        firstNameRule() {
            return this.formInputs.middleName &&
                this.formInputs.middleName.length > 0 &&
                this.formInputs.firstName !== "MARIA" &&
                this.formInputs.firstName !== "JOSE"
                ? "|first_name_curp:@curp"
                : "";
        },
        middleNameRule() {
            return this.formInputs.middleName &&
                this.formInputs.middleName.length > 0 &&
                (this.formInputs.firstName === "MARIA" ||
                    this.formInputs.firstName === "JOSE")
                ? "|first_name_curp:@curp"
                : "";
        },
        showInfoIcon() {
            return (
                this.formInputs.idType && this.formInputs.idType.id === "INE"
            );
        },
    },
    created() {
        this.$eventBus.$on("show-form-1", () => {
            this.showForm = true;
            this.formComplete = false;
        });
        this.$eventBus.$on("hide-forms", () => {
            this.showForm = false;
        });
        this.$eventBus.$on("complete-form-1", () => {
            this.showForm = false;
            this.formComplete = true;
        });
        this.$eventBus.$on("setFormsInputs", (inputs) => {
            this.setInputs(inputs);
        });
    },
};
</script>

<style>
</style>
